/**
 * Dashboard Page
 * Main dashboard showing key metrics and pending actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableHead,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, PriorityBadge, TablePagination } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, PrimaryButton } from '../../components/ui';
import { DashboardService } from '../../services/dashboard/DashboardService';
import type { PendingAction, DashboardKPI } from '../../types/dashboard';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { formatDateTime } from '@/utils/dateUtils';

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [kpis, setKpis] = useState<DashboardKPI[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isKpisLoading, setIsKpisLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalResults, setTotalResults] = useState(0);

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: theme.typography.body2.fontSize }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: theme.typography.body2.fontSize }} />;
      default:
        return <RemoveIcon sx={{ fontSize: theme.typography.body2.fontSize }} />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchKpis = async () => {
      try {
        setIsKpisLoading(true);
        const stats = await DashboardService.getStats();
        setKpis(stats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        setKpis([]);
      } finally {
        setIsKpisLoading(false);
      }
    };

    fetchKpis();
  }, []);

  const fetchPendingActions = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await DashboardService.getPendingActions(page, pageSize);
      setPendingActions(result.items);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch pending actions:', error);
      setPendingActions([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchPendingActions();
  }, [fetchPendingActions]);

  /**
   * Determine navigation route based on action type field
   */
  const getNavigationRoute = (action: PendingAction): string => {
    const actionType = action.type?.toLowerCase() || '';
    const entityId = action.id;

    switch (actionType) {
      case 'dispute':
        return `/disputes/${entityId}`;
      case 'complaint':
        return `/complaints/${entityId}`;
      case 'ghostverification':
        return `/ghost-verification/${entityId}`;
      case 'mission':
        return `/missions/${entityId}`;
      case 'contentmoderation':
        return `/content-moderation/${entityId}`;
      default:
        // Fallback based on caseSummary content
        const summary = action.caseSummary.toLowerCase();
        if (summary.includes('dispute')) return `/disputes/${entityId}`;
        if (summary.includes('complaint')) return `/complaints/${entityId}`;
        if (summary.includes('ghost')) return `/ghost-verification/${entityId}`;
        if (summary.includes('mission')) return `/missions/${entityId}`;
        return `/content-moderation/${entityId}`;
    }
  };

  const handleAction = (action: PendingAction) => {
    const route = getNavigationRoute(action);
    navigate(route);
  };

  const getActionButtonLabel = (_actionType: string): string => {
    return 'View';
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: theme.typography.h2.fontWeight,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Dashboard Overview
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          Monitor key metrics and pending actions across the platform.
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        {isKpisLoading ? (
          // Loading skeleton for KPIs
          [1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(2) }}>
                    <LoadingSpinner />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : kpis.length === 0 ? (
          // Empty state for KPIs
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                  No statistics available
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          kpis.map((kpi) => (
            <Grid item xs={12} sm={6} md={4} key={kpi.title}>
              <Card>
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: theme.spacing(1),
                    }}
                  >
                    {kpi.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                    <Typography variant="h4" sx={{ fontWeight: theme.typography.h4.fontWeight }}>
                      {kpi.value}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        color: getTrendColor(kpi.trend),
                      }}
                    >
                      {getTrendIcon(kpi.trend)}
                      <Typography variant="caption" sx={{ fontWeight: theme.typography.subtitle2.fontWeight }}>
                        {kpi.change}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Pending Actions Section */}
      <Typography variant="h6" sx={{ marginBottom: theme.spacing(2) }}>
        Pending Actions
      </Typography>

      {isLoading ? (
        <Card>
          <CardContent>
            <Box sx={{ padding: theme.spacing(4), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          </CardContent>
        </Card>
      ) : pendingActions.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              title="No Pending Actions"
              message="There are no pending actions at this time."
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent noPadding>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Summary</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Priority</TableHeaderCell>
                    <TableHeaderCell>Created At</TableHeaderCell>
                    <TableHeaderCell>Updated At</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingActions.map((action) => (
                    <TableRow key={action.id}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {action.caseSummary}
                      </TableCell>
                      <TableCell>
                        {action.type || '-'}
                      </TableCell>
                      <TableCell>
                        <PriorityBadge priority={action.priority} />
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.secondary }}>
                        {formatDateTime(action.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: theme.palette.text.secondary }}>
                        {formatDateTime(action.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <PrimaryButton
                          size="small"
                          onClick={() => handleAction(action)}
                          sx={{ padding: theme.spacing(0.5, 2) }}
                        >
                          {getActionButtonLabel(action.actionType)}
                        </PrimaryButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalResults > 0 && (
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={(e) => {
                  setPageSize(Number((e.target as any).value));
                  setPage(1);
                }}
                pageSizeOptions={[5, 10, 25]}
              />
            )}
          </CardContent>
        </Card>
      )}
    </MainLayout>
  );
};

export default DashboardPage;
